import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import Constants from '../../flux/Constants';
import styles from './Tooltip.module.less';

const TooltipContent = ({
	content,
	defaultIsVisible = false,
	id,
	position = Constants.Tooltip.place.Top,
	className = '',
	hideAfter = 5000,
	shouldShowTooltip = false,
	setShouldShowTooltip,
	targetRef,
}) => {
	const [isShown, setIsShown] = useState(defaultIsVisible);
	const [isVisible, setIsVisible] = useState(false);
	const tooltipRef = useRef(null);
	const scrollTimeoutRef = useRef(null);

	useEffect(() => {
		if (isShown && hideAfter > 0) {
			const timeout = setTimeout(() => {
				if (!shouldShowTooltip) {
					setIsShown(false);
				}
			}, hideAfter);
			return () => clearTimeout(timeout);
		}
	}, [isShown, hideAfter]);

	useEffect(() => {
		const handleScroll = () => {
			if (defaultIsVisible && tooltipRef.current && targetRef) {
				const newStyles = getStyles();
				Object.assign(tooltipRef.current.style, {
					top: newStyles.top,
					left: newStyles.left,
				});
			} else if (!defaultIsVisible && (shouldShowTooltip || isShown)) {
				setShouldShowTooltip(false);
				setIsShown(false);
			}
		};

		window.addEventListener('scroll', handleScroll, { passive: true });

		return () => {
			window.removeEventListener('scroll', handleScroll);
			if (scrollTimeoutRef.current) {
				clearTimeout(scrollTimeoutRef.current);
			}
		};
	}, [shouldShowTooltip, isShown, setShouldShowTooltip, defaultIsVisible]);

	useLayoutEffect(() => {
		if ((isShown || shouldShowTooltip) && tooltipRef.current) {
			setIsVisible(true);
		} else {
			setIsVisible(false);
		}
	}, [isShown, shouldShowTooltip]);

	const getTooltipClasses = () => {
		const classes = ['tooltip', `tooltip-${position}`];
		if (className) {
			classes.push(className);
		}
		return classes.map((cls) => styles[cls] || cls).join(' ');
	};

	const getStyles = () => {
		if ((isShown || shouldShowTooltip) && targetRef && tooltipRef.current) {
			const targetRect = targetRef.getBoundingClientRect();
			const tooltipRect = tooltipRef.current.getBoundingClientRect();
			let top;
			let left;

			switch (position) {
				case Constants.Tooltip.place.Top:
					top = targetRect.top - tooltipRect.height - 8;
					left = targetRect.left + (targetRect.width - tooltipRect.width) / 2;
					break;
				case Constants.Tooltip.place.Bottom:
					top = targetRect.bottom + 8;
					left = targetRect.left + (targetRect.width - tooltipRect.width) / 2;
					break;
				case Constants.Tooltip.place.Left:
					top = targetRect.top + (targetRect.height - tooltipRect.height) / 2;
					left = targetRect.left - tooltipRect.width - 8;
					break;
				case Constants.Tooltip.place.Right:
					top = targetRect.top + (targetRect.height - tooltipRect.height) / 2;
					left = targetRect.right + 8;
					break;
				default:
					top = targetRect.top - tooltipRect.height - 8;
					left = targetRect.left + (targetRect.width - tooltipRect.width) / 2;
			}

			const padding = 8;
			if (left < padding) {
				left = padding;
			}
			if (left + tooltipRect.width > window.innerWidth - padding) {
				left = window.innerWidth - tooltipRect.width - padding;
			}

			if (top < padding) {
				top = padding;
			}
			if (top + tooltipRect.height > window.innerHeight - padding) {
				top = window.innerHeight - tooltipRect.height - padding;
			}

			return {
				position: 'fixed',
				top: `${Math.round(top)}px`,
				left: `${Math.round(left)}px`,
				opacity: isVisible ? 1 : 0,
				pointerEvents: isVisible ? 'auto' : 'none',
				transition: 'opacity 0.1s',
			};
		}
		return { opacity: 0, pointerEvents: 'none' };
	};

	if (!content) {
		return null;
	}

	return (
		<>
			{(isShown || shouldShowTooltip) && (
				<div
					id={id}
					ref={tooltipRef}
					className={getTooltipClasses()}
					style={getStyles()}
					role="tooltip"
					aria-hidden={!isVisible}
					onMouseEnter={(e) => {
						e.stopPropagation();
						setShouldShowTooltip(false);
					}}
				>
					{content}
					<div className={styles['tooltip-arrow']} />
				</div>
			)}
		</>
	);
};

TooltipContent.propTypes = {
	content: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
	id: PropTypes.string,
	className: PropTypes.string,
	defaultIsVisible: PropTypes.bool,
	hideAfter: PropTypes.number,
	position: PropTypes.oneOf(Object.values(Constants.Tooltip.place)),
	targetRef: PropTypes.object,
	shouldShowTooltip: PropTypes.bool,
	setShouldShowTooltip: PropTypes.func.isRequired,
};

export default TooltipContent;
